import React, { useState, useEffect, useRef } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Checkbox } from './ui/checkbox';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import { toast } from 'sonner';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import MARSection from './MARSection';
import CarePlanSection from './CarePlanSection';
import WoundCareSection from './WoundCareSection';
import {
  Mic, MicOff, Square, Play, Loader2, Check, X,
  User, Heart, Pill, AlertTriangle, FileText, Activity,
  Clipboard, Phone, Stethoscope, Bandage, Users, Calendar,
  ChevronRight, Clock, AlertCircle, Shield, Edit2, Plus,
  TrendingUp, Thermometer, Droplet, Wind, Download, Save,
  Sparkles, ArrowRight, Ruler
} from 'lucide-react';

// =====================================================
// VOICE RECORDER COMPONENT
// =====================================================

function VoiceRecorder({ clientId, shiftId, onNoteConfirmed, onSuggestedTemplate, onWoundData }) {
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [audioBlob, setAudioBlob] = useState(null);
  const [transcription, setTranscription] = useState('');
  const [clinicalNote, setClinicalNote] = useState('');
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [showReview, setShowReview] = useState(false);
  const [suggestedTemplate, setSuggestedTemplate] = useState(null);
  const [woundData, setWoundData] = useState(null);
  
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const timerRef = useRef(null);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (mediaRecorderRef.current && isRecording) {
        mediaRecorderRef.current.stop();
      }
    };
  }, [isRecording]);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorderRef.current = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      audioChunksRef.current = [];

      mediaRecorderRef.current.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorderRef.current.onstop = () => {
        const blob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        setAudioBlob(blob);
        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorderRef.current.start(1000);
      setIsRecording(true);
      setRecordingTime(0);
      
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);

      toast.success('Recording started');
    } catch (error) {
      toast.error('Could not access microphone');
      console.error('Microphone access error:', error);
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      toast.info('Recording stopped');
    }
  };

  const cancelRecording = () => {
    stopRecording();
    setAudioBlob(null);
    setTranscription('');
    setClinicalNote('');
    setShowReview(false);
    setRecordingTime(0);
    setSuggestedTemplate(null);
    setWoundData(null);
  };

  const transcribeAudio = async () => {
    if (!audioBlob) {
      toast.error('No recording to transcribe');
      return;
    }

    setIsTranscribing(true);
    try {
      const formData = new FormData();
      formData.append('audio', audioBlob, 'recording.webm');
      formData.append('client_id', clientId);
      if (shiftId) formData.append('shift_id', shiftId);

      const response = await api.post('/emr/voice/transcribe', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data.success) {
        setTranscription(response.data.transcription);
        toast.success('Transcription complete');
        // Automatically generate clinical note
        await generateClinicalNote(response.data.transcription);
      }
    } catch (error) {
      toast.error('Transcription failed');
      console.error('Transcription error:', error);
    } finally {
      setIsTranscribing(false);
    }
  };

  const generateClinicalNote = async (rawText) => {
    setIsGenerating(true);
    try {
      const formData = new FormData();
      formData.append('client_id', clientId);
      formData.append('transcription', rawText || transcription);
      if (shiftId) formData.append('shift_id', shiftId);

      const response = await api.post('/emr/voice/generate-note', formData);

      if (response.data.success) {
        setClinicalNote(response.data.clinical_note);
        setShowReview(true);
        
        // Handle AI suggestions
        if (response.data.suggested_template) {
          setSuggestedTemplate(response.data.suggested_template);
          // Notify parent component
          onSuggestedTemplate && onSuggestedTemplate(response.data.suggested_template);
        }
        
        if (response.data.wound_data?.has_wound_data) {
          setWoundData(response.data.wound_data);
          // Notify parent component
          onWoundData && onWoundData(response.data.wound_data);
        }
        
        toast.success('Clinical note generated');
      }
    } catch (error) {
      toast.error('Note generation failed');
      console.error('Note generation error:', error);
    } finally {
      setIsGenerating(false);
    }
  };

  const confirmNote = async () => {
    try {
      const response = await api.post('/emr/voice/confirm-note', {
        client_id: clientId,
        shift_id: shiftId,
        raw_transcription: transcription,
        ai_cleaned_note: clinicalNote,
        note_type: 'daily'
      });

      if (response.data.success) {
        toast.success('Progress note saved and signed');
        
        // Pass suggested template and wound data along with the response
        const enrichedResponse = {
          ...response.data,
          suggestedTemplate,
          woundData
        };
        onNoteConfirmed && onNoteConfirmed(enrichedResponse);
        cancelRecording();
      }
    } catch (error) {
      toast.error('Failed to save note');
      console.error('Save error:', error);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="space-y-4">
      {/* Recording Controls */}
      <div className="flex items-center gap-4">
        {!isRecording && !audioBlob && (
          <Button
            onClick={startRecording}
            className="bg-red-500 hover:bg-red-600 text-white flex items-center gap-2"
            data-testid="start-recording-btn"
          >
            <Mic className="w-5 h-5" />
            Start Voice Note
          </Button>
        )}

        {isRecording && (
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 px-4 py-2 bg-red-100 rounded-full animate-pulse">
              <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse" />
              <span className="font-mono text-red-700 font-medium">
                Recording {formatTime(recordingTime)}
              </span>
            </div>
            <Button
              onClick={stopRecording}
              variant="outline"
              className="border-red-500 text-red-500"
              data-testid="stop-recording-btn"
            >
              <Square className="w-4 h-4 mr-2" />
              Stop
            </Button>
            <Button
              onClick={cancelRecording}
              variant="ghost"
              className="text-gray-500"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        )}

        {audioBlob && !isRecording && !showReview && (
          <div className="flex items-center gap-4">
            <span className="text-sm text-gray-600">
              Recording ready ({formatTime(recordingTime)})
            </span>
            <Button
              onClick={transcribeAudio}
              disabled={isTranscribing || isGenerating}
              className="bg-purple-600 hover:bg-purple-700 text-white"
              data-testid="transcribe-btn"
            >
              {isTranscribing ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Transcribing...
                </>
              ) : isGenerating ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Generating Note...
                </>
              ) : (
                <>
                  <FileText className="w-4 h-4 mr-2" />
                  Process Recording
                </>
              )}
            </Button>
            <Button
              onClick={cancelRecording}
              variant="ghost"
              className="text-gray-500"
            >
              Cancel
            </Button>
          </div>
        )}
      </div>

      {/* Review Modal */}
      {showReview && (
        <Card className="p-6 bg-purple-50 border-purple-200">
          <h4 className="font-semibold text-purple-900 mb-4 flex items-center gap-2">
            <Shield className="w-5 h-5" />
            Review AI-Generated Clinical Note
          </h4>

          {/* Raw Transcription */}
          <div className="mb-4">
            <Label className="text-sm text-gray-600">Raw Transcription:</Label>
            <div className="mt-1 p-3 bg-white rounded border text-sm text-gray-700 max-h-32 overflow-y-auto">
              {transcription}
            </div>
          </div>

          {/* AI Clinical Note */}
          <div className="mb-4">
            <Label className="text-sm text-gray-600">AI-Generated Clinical Note:</Label>
            <Textarea
              value={clinicalNote}
              onChange={(e) => setClinicalNote(e.target.value)}
              rows={8}
              className="mt-1 bg-white"
              data-testid="clinical-note-textarea"
            />
            <p className="text-xs text-gray-500 mt-1">
              You may edit the note before confirming. Once confirmed, it will be signed with your name and timestamp.
            </p>
          </div>

          {/* AI Suggestions Section */}
          {(suggestedTemplate || woundData?.has_wound_data) && (
            <div className="mb-4 space-y-3">
              <Label className="text-sm text-purple-700 flex items-center gap-2">
                <Sparkles className="w-4 h-4" />
                AI Detected Insights
              </Label>
              
              {/* Suggested Care Plan Template */}
              {suggestedTemplate && (
                <div 
                  className="p-3 bg-green-50 border border-green-200 rounded-lg flex items-center justify-between"
                  data-testid="ai-suggested-template-card"
                >
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-green-100 rounded-full">
                      <Heart className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-green-800">Care Plan Template Suggested</p>
                      <p className="text-xs text-green-600">
                        AI detected content related to: <span className="font-semibold">{suggestedTemplate.replace(/_/g, ' ')}</span>
                      </p>
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-green-500 text-green-600 hover:bg-green-50"
                    onClick={() => {
                      onSuggestedTemplate && onSuggestedTemplate(suggestedTemplate);
                      toast.info(`Navigate to Care Plan tab to add ${suggestedTemplate.replace(/_/g, ' ')} template`);
                    }}
                    data-testid="add-template-from-ai-btn"
                  >
                    <ArrowRight className="w-4 h-4 mr-1" />
                    View in Care Plan
                  </Button>
                </div>
              )}
              
              {/* Wound Data Extracted */}
              {woundData?.has_wound_data && woundData.wounds?.length > 0 && (
                <div 
                  className="p-3 bg-orange-50 border border-orange-200 rounded-lg"
                  data-testid="ai-wound-data-card"
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-orange-100 rounded-full">
                        <Bandage className="w-5 h-5 text-orange-600" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-orange-800">Wound Measurements Detected</p>
                        <p className="text-xs text-orange-600">
                          AI extracted {woundData.wounds.length} wound{woundData.wounds.length > 1 ? 's' : ''} from your notes
                        </p>
                      </div>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-orange-500 text-orange-600 hover:bg-orange-50"
                      onClick={() => {
                        onWoundData && onWoundData(woundData);
                        toast.info('Navigate to Wound Care tab to create assessment with these measurements');
                      }}
                      data-testid="create-wound-from-ai-btn"
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Create Wound Assessment
                    </Button>
                  </div>
                  
                  {/* Wound Details */}
                  <div className="mt-2 space-y-2">
                    {woundData.wounds.map((wound, idx) => (
                      <div key={idx} className="p-2 bg-white rounded border border-orange-100 text-sm">
                        <div className="flex items-center gap-2 text-orange-700">
                          <Ruler className="w-4 h-4" />
                          <span className="font-medium">{wound.location || 'Unknown location'}</span>
                          {wound.stage && <span className="px-2 py-0.5 bg-orange-100 rounded text-xs">Stage {wound.stage}</span>}
                        </div>
                        {(wound.length_cm || wound.width_cm || wound.depth_cm) && (
                          <p className="text-xs text-gray-600 mt-1 ml-6">
                            Dimensions: {wound.length_cm || '?'}cm × {wound.width_cm || '?'}cm × {wound.depth_cm || '?'}cm
                            {wound.length_cm && wound.width_cm && wound.depth_cm && (
                              <span className="ml-2 text-orange-600">
                                (Volume: {(wound.length_cm * wound.width_cm * wound.depth_cm).toFixed(2)} cm³)
                              </span>
                            )}
                          </p>
                        )}
                        {wound.characteristics && (
                          <p className="text-xs text-gray-500 mt-1 ml-6">{wound.characteristics}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Confirm/Cancel Buttons */}
          <div className="flex justify-end gap-3">
            <Button
              onClick={cancelRecording}
              variant="outline"
            >
              Discard
            </Button>
            <Button
              onClick={confirmNote}
              className="bg-green-600 hover:bg-green-700 text-white"
              data-testid="confirm-note-btn"
            >
              <Check className="w-4 h-4 mr-2" />
              Confirm & Sign
            </Button>
          </div>
        </Card>
      )}
    </div>
  );
}

// =====================================================
// MAIN EMR DASHBOARD COMPONENT
// =====================================================

export default function EMRDashboard({ clientId, shiftId }) {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState('overview');
  const [overview, setOverview] = useState(null);
  const [medicalHistory, setMedicalHistory] = useState([]);
  const [medications, setMedications] = useState([]);
  const [allergies, setAllergies] = useState([]);
  const [vitals, setVitals] = useState([]);
  const [progressNotes, setProgressNotes] = useState([]);
  const [carePlan, setCarePlan] = useState(null);
  const [incidents, setIncidents] = useState([]);
  
  // Edit Mode State (Admin/RN only)
  const [isEditMode, setIsEditMode] = useState(false);
  const [savingOverview, setSavingOverview] = useState(false);
  const [editForm, setEditForm] = useState({
    primary_diagnosis: '',
    emergency_contact_name: '',
    emergency_contact_phone: '',
    emergency_contact_relationship: '',
    address: '',
    fall_risk_factors: []
  });
  const [fallRiskCategories, setFallRiskCategories] = useState({});
  
  // Allergy Edit State
  const [editingAllergies, setEditingAllergies] = useState([]);
  const [showAddAllergy, setShowAddAllergy] = useState(false);
  const [newAllergy, setNewAllergy] = useState({
    allergen: '',
    severity: 'mild',
    reaction: '',
    notes: ''
  });
  const [savingAllergy, setSavingAllergy] = useState(false);
  const [deletingAllergyId, setDeletingAllergyId] = useState(null);
  
  // Record Vitals Form State
  const [showVitalsForm, setShowVitalsForm] = useState(false);
  const [savingVitals, setSavingVitals] = useState(false);
  const [vitalsForm, setVitalsForm] = useState({
    blood_pressure_systolic: '',
    blood_pressure_diastolic: '',
    heart_rate: '',
    respiratory_rate: '',
    temperature: '',
    oxygen_saturation: '',
    blood_glucose: '',
    pain_level: '',
    weight: '',
    notes: ''
  });

  // PDF Export State
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [exportOption, setExportOption] = useState('30days'); // '30days' | 'all'
  
  // AI Suggestion State (from Voice Charting)
  const [aiSuggestedTemplate, setAiSuggestedTemplate] = useState(null);
  const [aiWoundData, setAiWoundData] = useState(null);
  const [showTemplatePrompt, setShowTemplatePrompt] = useState(false);
  const [pendingTemplate, setPendingTemplate] = useState(null);
  
  // Check if user can edit (admin or RN)
  const canEdit = user && ['admin', 'super_admin', 'rn'].includes(user.role);

  const fetchEMRData = async () => {
    setLoading(true);
    try {
      const [
        overviewRes,
        historyRes,
        medsRes,
        allergiesRes,
        vitalsRes,
        notesRes,
        carePlanRes,
        incidentsRes
      ] = await Promise.all([
        api.get(`/emr/client/${clientId}/overview`).catch(() => ({ data: null })),
        api.get(`/emr/client/${clientId}/medical-history`).catch(() => ({ data: [] })),
        api.get(`/emr/client/${clientId}/medications`).catch(() => ({ data: [] })),
        api.get(`/emr/client/${clientId}/allergies`).catch(() => ({ data: [] })),
        api.get(`/emr/client/${clientId}/vitals?limit=10`).catch(() => ({ data: [] })),
        api.get(`/emr/client/${clientId}/progress-notes?limit=20`).catch(() => ({ data: [] })),
        api.get(`/emr/client/${clientId}/care-plan`).catch(() => ({ data: null })),
        api.get(`/emr/client/${clientId}/incidents`).catch(() => ({ data: [] }))
      ]);

      setOverview(overviewRes.data);
      setMedicalHistory(historyRes.data || []);
      setMedications(medsRes.data || []);
      setAllergies(allergiesRes.data || []);
      setVitals(vitalsRes.data || []);
      setProgressNotes(notesRes.data || []);
      setCarePlan(carePlanRes.data);
      setIncidents(incidentsRes.data || []);
      
      // Initialize edit form with current data
      if (overviewRes.data) {
        setEditForm({
          primary_diagnosis: overviewRes.data.primary_diagnosis || '',
          emergency_contact_name: overviewRes.data.emergency_contact?.name || '',
          emergency_contact_phone: overviewRes.data.emergency_contact?.phone || '',
          emergency_contact_relationship: overviewRes.data.emergency_contact?.relationship || '',
          address: overviewRes.data.address || '',
          fall_risk_factors: overviewRes.data.fall_risk_factors || []
        });
      }
    } catch (error) {
      console.error('Failed to load EMR data:', error);
      toast.error('Failed to load EMR data');
    } finally {
      setLoading(false);
    }
  };

  // Fetch fall risk categories
  const fetchFallRiskCategories = async () => {
    try {
      const response = await api.get('/emr/fall-risk-categories');
      setFallRiskCategories(response.data || {});
    } catch (error) {
      console.error('Failed to load fall risk categories:', error);
    }
  };

  useEffect(() => {
    if (clientId) {
      fetchEMRData();
      fetchFallRiskCategories();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  // Save overview changes
  const saveOverviewChanges = async () => {
    setSavingOverview(true);
    try {
      await api.put(`/emr/client/${clientId}/overview`, editForm);
      toast.success('Client overview updated successfully');
      setIsEditMode(false);
      setShowAddAllergy(false);
      await fetchEMRData(); // Refresh data
    } catch (error) {
      console.error('Failed to update overview:', error);
      toast.error(error.response?.data?.detail || 'Failed to update overview');
    } finally {
      setSavingOverview(false);
    }
  };

  // Toggle fall risk factor
  const toggleFallRiskFactor = (factorId) => {
    setEditForm(prev => ({
      ...prev,
      fall_risk_factors: prev.fall_risk_factors.includes(factorId)
        ? prev.fall_risk_factors.filter(f => f !== factorId)
        : [...prev.fall_risk_factors, factorId]
    }));
  };

  // Calculate fall risk level from factors
  const getFallRiskLevel = (factors = []) => {
    const count = factors.length;
    if (count >= 4) return { level: 'high', color: 'bg-red-100 text-red-800' };
    if (count >= 2) return { level: 'moderate', color: 'bg-amber-100 text-amber-800' };
    return { level: 'low', color: 'bg-green-100 text-green-800' };
  };

  // Get readable factor labels
  const getFactorLabels = (factorIds = []) => {
    const labels = [];
    Object.entries(fallRiskCategories).forEach(([category, factors]) => {
      factors.forEach(factor => {
        if (factorIds.includes(factor.id)) {
          labels.push(factor.label);
        }
      });
    });
    return labels;
  };

  // =====================================================
  // ALLERGY MANAGEMENT FUNCTIONS
  // =====================================================
  
  // Enter edit mode - also copy allergies for editing
  const enterEditMode = () => {
    setEditForm({
      primary_diagnosis: overview?.primary_diagnosis || '',
      emergency_contact_name: overview?.emergency_contact?.name || '',
      emergency_contact_phone: overview?.emergency_contact?.phone || '',
      emergency_contact_relationship: overview?.emergency_contact?.relationship || '',
      address: overview?.address || '',
      fall_risk_factors: overview?.fall_risk_factors || []
    });
    setEditingAllergies([...allergies]);
    setIsEditMode(true);
  };

  // Cancel edit mode
  const cancelEditMode = () => {
    setIsEditMode(false);
    setShowAddAllergy(false);
    setNewAllergy({ allergen: '', severity: 'mild', reaction: '', notes: '' });
  };

  // Add new allergy
  const handleAddAllergy = async () => {
    if (!newAllergy.allergen.trim()) {
      toast.error('Allergen name is required');
      return;
    }
    
    setSavingAllergy(true);
    try {
      const response = await api.post(`/emr/client/${clientId}/allergies`, newAllergy);
      setAllergies(prev => [...prev, response.data]);
      setEditingAllergies(prev => [...prev, response.data]);
      setNewAllergy({ allergen: '', severity: 'mild', reaction: '', notes: '' });
      setShowAddAllergy(false);
      toast.success('Allergy added successfully');
    } catch (error) {
      console.error('Failed to add allergy:', error);
      toast.error(error.response?.data?.detail || 'Failed to add allergy');
    } finally {
      setSavingAllergy(false);
    }
  };

  // Update existing allergy
  const handleUpdateAllergy = async (allergyId, updates) => {
    try {
      const response = await api.put(`/emr/client/${clientId}/allergies/${allergyId}`, updates);
      setAllergies(prev => prev.map(a => a.id === allergyId ? response.data : a));
      setEditingAllergies(prev => prev.map(a => a.id === allergyId ? response.data : a));
      toast.success('Allergy updated');
    } catch (error) {
      console.error('Failed to update allergy:', error);
      toast.error('Failed to update allergy');
    }
  };

  // Delete allergy
  const handleDeleteAllergy = async (allergyId) => {
    setDeletingAllergyId(allergyId);
    try {
      await api.delete(`/emr/client/${clientId}/allergies/${allergyId}`);
      setAllergies(prev => prev.filter(a => a.id !== allergyId));
      setEditingAllergies(prev => prev.filter(a => a.id !== allergyId));
      toast.success('Allergy removed');
    } catch (error) {
      console.error('Failed to delete allergy:', error);
      toast.error('Failed to remove allergy');
    } finally {
      setDeletingAllergyId(null);
    }
  };

  // Update allergy in editing state (before save)
  const updateEditingAllergy = (allergyId, field, value) => {
    setEditingAllergies(prev => prev.map(a => 
      a.id === allergyId ? { ...a, [field]: value } : a
    ));
  };

  const handleNoteConfirmed = (responseData) => {
    fetchEMRData();
    
    // Show template prompt if AI suggested one
    if (responseData?.suggestedTemplate) {
      setPendingTemplate(responseData.suggestedTemplate);
      setShowTemplatePrompt(true);
    }
    
    // Store wound data for later use
    if (responseData?.woundData?.has_wound_data) {
      setAiWoundData(responseData.woundData);
    }
  };

  const getTemplateDisplayName = (template) => {
    const names = {
      'SKIN_INTEGRITY': 'Skin Integrity/Wound Care',
      'FALL_RISK': 'Fall Prevention',
      'MEDICATION_MANAGEMENT': 'Medication Management',
      'NUTRITION': 'Nutrition Support',
      'COGNITIVE': 'Dementia/Cognitive Support',
      'MOBILITY': 'Mobility Enhancement',
      'PAIN_MANAGEMENT': 'Pain Management',
      'DEPRESSION_ANXIETY': 'Behavioral/Mental Health'
    };
    return names[template] || template.replace(/_/g, ' ');
  };

  const handleAddTemplateFromPrompt = () => {
    setShowTemplatePrompt(false);
    setAiSuggestedTemplate(pendingTemplate);
    setActiveSection('care-plan');
    toast.info('Navigate to Care Plan - select "Add from Template" to add the suggested template');
  };

  const handleVitalsSubmit = async (e) => {
    e.preventDefault();
    setSavingVitals(true);
    
    try {
      // Build the vitals data object, only include non-empty values
      const vitalsData = {
        client_id: clientId,
        shift_id: shiftId || null
      };
      
      if (vitalsForm.blood_pressure_systolic) vitalsData.blood_pressure_systolic = parseInt(vitalsForm.blood_pressure_systolic);
      if (vitalsForm.blood_pressure_diastolic) vitalsData.blood_pressure_diastolic = parseInt(vitalsForm.blood_pressure_diastolic);
      if (vitalsForm.heart_rate) vitalsData.heart_rate = parseInt(vitalsForm.heart_rate);
      if (vitalsForm.respiratory_rate) vitalsData.respiratory_rate = parseInt(vitalsForm.respiratory_rate);
      if (vitalsForm.temperature) vitalsData.temperature = parseFloat(vitalsForm.temperature);
      if (vitalsForm.oxygen_saturation) vitalsData.oxygen_saturation = parseInt(vitalsForm.oxygen_saturation);
      if (vitalsForm.blood_glucose) vitalsData.blood_glucose = parseInt(vitalsForm.blood_glucose);
      if (vitalsForm.pain_level !== '') vitalsData.pain_level = parseInt(vitalsForm.pain_level);
      if (vitalsForm.weight) vitalsData.weight = parseFloat(vitalsForm.weight);
      if (vitalsForm.notes) vitalsData.notes = vitalsForm.notes;
      
      await api.post(`/emr/client/${clientId}/vitals`, vitalsData);
      toast.success('Vital signs recorded successfully');
      
      // Reset form and close
      setVitalsForm({
        blood_pressure_systolic: '',
        blood_pressure_diastolic: '',
        heart_rate: '',
        respiratory_rate: '',
        temperature: '',
        oxygen_saturation: '',
        blood_glucose: '',
        pain_level: '',
        weight: '',
        notes: ''
      });
      setShowVitalsForm(false);
      fetchEMRData();
    } catch (error) {
      console.error('Failed to save vitals:', error);
      toast.error('Failed to record vital signs');
    } finally {
      setSavingVitals(false);
    }
  };

  const handleExportPdf = async () => {
    setExportingPdf(true);
    try {
      const includeAll = exportOption === 'all';
      const response = await api.get(`/emr/client/${clientId}/export-pdf`, {
        params: {
          include_all_notes: includeAll,
          days_of_notes: 30
        },
        responseType: 'blob'
      });
      
      // Create download link
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `EMR_${overview?.client_name || 'Report'}_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('EMR PDF downloaded successfully');
      setShowExportModal(false);
    } catch (error) {
      console.error('Failed to export PDF:', error);
      toast.error('Failed to export EMR PDF');
    } finally {
      setExportingPdf(false);
    }
  };

  const sections = [
    { id: 'overview', label: 'Overview', icon: User },
    { id: 'vitals', label: 'Vital Signs', icon: Activity },
    { id: 'notes', label: 'Progress Notes', icon: FileText },
    { id: 'medications', label: 'Medications', icon: Pill },
    { id: 'history', label: 'Medical History', icon: Clipboard },
    { id: 'care-plan', label: 'Care Plan', icon: Heart },
    { id: 'incidents', label: 'Incidents', icon: AlertTriangle },
    { id: 'wound-care', label: 'Wound Care', icon: Bandage },
    { id: 'family', label: 'Family Log', icon: Users },
  ];

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  if (!overview) {
    return (
      <Card className="p-8 text-center">
        <AlertCircle className="w-12 h-12 text-amber-500 mx-auto mb-3" />
        <h3 className="font-semibold text-gray-900">EMR Not Initialized</h3>
        <p className="text-gray-600 mt-1">Contact an administrator to set up this client's EMR.</p>
      </Card>
    );
  }

  return (
    <div className="space-y-6" data-testid="emr-dashboard">
      {/* Client Header */}
      <Card className="p-4 bg-gradient-to-r from-purple-600 to-purple-800 text-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center">
              <User className="w-8 h-8" />
            </div>
            <div>
              <h2 className="text-2xl font-bold">{overview.client_name}</h2>
              <p className="text-purple-200">DOB: {overview.date_of_birth || 'N/A'}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {allergies.length > 0 && (
              <div className="flex items-center gap-2 bg-red-500 px-3 py-1 rounded-full text-sm">
                <AlertTriangle className="w-4 h-4" />
                {allergies.length} Allergies
              </div>
            )}
            <Button
              onClick={() => setShowExportModal(true)}
              variant="outline"
              className="bg-white/10 border-white/30 text-white hover:bg-white/20"
              data-testid="export-emr-pdf-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Export PDF
            </Button>
          </div>
        </div>
      </Card>

      {/* Export PDF Modal */}
      {showExportModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md p-6 m-4">
            <h3 className="text-xl font-semibold text-purple-900 mb-4">Export EMR Report</h3>
            <p className="text-gray-600 mb-4">
              Generate a comprehensive PDF report of this client's Electronic Medical Record.
            </p>
            
            <div className="space-y-3 mb-6">
              <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-purple-50">
                <input
                  type="radio"
                  name="exportOption"
                  value="30days"
                  checked={exportOption === '30days'}
                  onChange={() => setExportOption('30days')}
                  className="w-4 h-4 text-purple-600"
                />
                <div>
                  <span className="font-medium">Last 30 Days</span>
                  <p className="text-sm text-gray-500">Progress notes from the past 30 days</p>
                </div>
              </label>
              <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-purple-50">
                <input
                  type="radio"
                  name="exportOption"
                  value="all"
                  checked={exportOption === 'all'}
                  onChange={() => setExportOption('all')}
                  className="w-4 h-4 text-purple-600"
                />
                <div>
                  <span className="font-medium">All Progress Notes</span>
                  <p className="text-sm text-gray-500">Include complete note history</p>
                </div>
              </label>
            </div>
            
            <div className="flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => setShowExportModal(false)}
                disabled={exportingPdf}
              >
                Cancel
              </Button>
              <Button
                onClick={handleExportPdf}
                className="bg-purple-600 hover:bg-purple-700 text-white"
                disabled={exportingPdf}
                data-testid="confirm-export-pdf-btn"
              >
                {exportingPdf ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Download className="w-4 h-4 mr-2" />
                    Download PDF
                  </>
                )}
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* Section Navigation */}
      <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
        {sections.map(section => (
          <button
            key={section.id}
            onClick={() => setActiveSection(section.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg whitespace-nowrap transition-colors ${
              activeSection === section.id
                ? 'bg-purple-600 text-white'
                : 'bg-white border border-gray-200 text-gray-700 hover:bg-purple-50'
            }`}
          >
            <section.icon className="w-4 h-4" />
            {section.label}
          </button>
        ))}
      </div>

      {/* Content Sections */}
      {activeSection === 'overview' && (
        <TooltipProvider>
          <div className="space-y-4">
            {/* Edit Mode Header - Admin/RN Only */}
            {canEdit && (
              <div className="flex justify-end">
                {isEditMode ? (
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      onClick={cancelEditMode}
                      disabled={savingOverview}
                    >
                      <X className="w-4 h-4 mr-2" />
                      Cancel
                    </Button>
                    <Button
                      onClick={saveOverviewChanges}
                      disabled={savingOverview}
                      className="bg-green-600 hover:bg-green-700 text-white"
                    >
                      {savingOverview ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Save className="w-4 h-4 mr-2" />
                      )}
                      Save Changes
                    </Button>
                  </div>
                ) : (
                  <Button
                    onClick={enterEditMode}
                    variant="outline"
                    className="text-purple-600 border-purple-300 hover:bg-purple-50"
                  >
                    <Edit2 className="w-4 h-4 mr-2" />
                    Edit Overview
                  </Button>
                )}
              </div>
            )}

            {/* Overview Cards Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {/* Allergies Card with Edit Mode */}
              <Card className="p-4 border-red-200 bg-red-50 lg:col-span-2">
                <h3 className="font-semibold text-red-800 flex items-center gap-2 mb-3">
                  <AlertTriangle className="w-5 h-5" />
                  Allergies
                  {isEditMode && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setShowAddAllergy(true)}
                      className="ml-auto text-red-700 hover:bg-red-100"
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Add
                    </Button>
                  )}
                </h3>
                
                {/* View Mode - Pills with Tooltips */}
                {!isEditMode && (
                  allergies.length === 0 ? (
                    <p className="text-gray-500 text-sm">No known allergies</p>
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {allergies.map(a => (
                        <Tooltip key={a.id}>
                          <TooltipTrigger asChild>
                            <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium cursor-help ${
                              a.severity === 'severe' ? 'bg-red-200 text-red-800' :
                              a.severity === 'moderate' ? 'bg-amber-200 text-amber-800' :
                              'bg-yellow-100 text-yellow-800'
                            }`}>
                              {a.allergen}
                            </span>
                          </TooltipTrigger>
                          <TooltipContent className="bg-white border shadow-lg p-3 max-w-xs">
                            <div className="text-sm">
                              <p className="font-semibold text-gray-900">{a.allergen}</p>
                              <p className="text-gray-600 mt-1">
                                <span className="font-medium">Severity:</span> {a.severity}
                              </p>
                              {a.reaction && (
                                <p className="text-gray-600">
                                  <span className="font-medium">Reaction:</span> {a.reaction}
                                </p>
                              )}
                              {a.notes && (
                                <p className="text-gray-500 mt-1 text-xs italic">{a.notes}</p>
                              )}
                            </div>
                          </TooltipContent>
                        </Tooltip>
                      ))}
                    </div>
                  )
                )}
                
                {/* Edit Mode - Editable List */}
                {isEditMode && (
                  <div className="space-y-3">
                    {allergies.length === 0 && !showAddAllergy && (
                      <p className="text-gray-500 text-sm">No known allergies. Click "Add" to add one.</p>
                    )}
                    
                    {allergies.map(allergy => (
                      <div key={allergy.id} className="bg-white p-3 rounded-lg border border-red-200 space-y-2">
                        <div className="flex items-center gap-2">
                          <Input
                            value={allergy.allergen}
                            onChange={(e) => handleUpdateAllergy(allergy.id, { allergen: e.target.value })}
                            placeholder="Allergen name"
                            className="flex-1 text-sm"
                          />
                          <select
                            value={allergy.severity}
                            onChange={(e) => handleUpdateAllergy(allergy.id, { severity: e.target.value })}
                            className="px-2 py-1 text-sm border rounded-md"
                          >
                            <option value="mild">Mild</option>
                            <option value="moderate">Moderate</option>
                            <option value="severe">Severe</option>
                          </select>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDeleteAllergy(allergy.id)}
                            disabled={deletingAllergyId === allergy.id}
                            className="text-red-600 hover:bg-red-100"
                          >
                            {deletingAllergyId === allergy.id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <X className="w-4 h-4" />
                            )}
                          </Button>
                        </div>
                        <Input
                          value={allergy.reaction || ''}
                          onChange={(e) => handleUpdateAllergy(allergy.id, { reaction: e.target.value })}
                          placeholder="Reaction (e.g., hives, anaphylaxis)"
                          className="text-sm"
                        />
                        <Input
                          value={allergy.notes || ''}
                          onChange={(e) => handleUpdateAllergy(allergy.id, { notes: e.target.value })}
                          placeholder="Admin notes"
                          className="text-sm text-gray-600"
                        />
                      </div>
                    ))}
                    
                    {/* Add New Allergy Form */}
                    {showAddAllergy && (
                      <div className="bg-white p-3 rounded-lg border-2 border-dashed border-red-300 space-y-2">
                        <p className="text-sm font-medium text-red-800 mb-2">Add New Allergy</p>
                        <div className="flex items-center gap-2">
                          <Input
                            value={newAllergy.allergen}
                            onChange={(e) => setNewAllergy(prev => ({ ...prev, allergen: e.target.value }))}
                            placeholder="Allergen name *"
                            className="flex-1 text-sm"
                          />
                          <select
                            value={newAllergy.severity}
                            onChange={(e) => setNewAllergy(prev => ({ ...prev, severity: e.target.value }))}
                            className="px-2 py-1 text-sm border rounded-md"
                          >
                            <option value="mild">Mild</option>
                            <option value="moderate">Moderate</option>
                            <option value="severe">Severe</option>
                          </select>
                        </div>
                        <Input
                          value={newAllergy.reaction}
                          onChange={(e) => setNewAllergy(prev => ({ ...prev, reaction: e.target.value }))}
                          placeholder="Reaction (e.g., hives, anaphylaxis)"
                          className="text-sm"
                        />
                        <Input
                          value={newAllergy.notes}
                          onChange={(e) => setNewAllergy(prev => ({ ...prev, notes: e.target.value }))}
                          placeholder="Admin notes"
                          className="text-sm"
                        />
                        <div className="flex gap-2 justify-end">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setShowAddAllergy(false);
                              setNewAllergy({ allergen: '', severity: 'mild', reaction: '', notes: '' });
                            }}
                          >
                            Cancel
                          </Button>
                          <Button
                            size="sm"
                            onClick={handleAddAllergy}
                            disabled={savingAllergy || !newAllergy.allergen.trim()}
                            className="bg-red-600 hover:bg-red-700 text-white"
                          >
                            {savingAllergy ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              'Add Allergy'
                            )}
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </Card>

              {/* Primary Diagnosis Card */}
              <Card className="p-4 border-purple-200">
                <h3 className="font-semibold text-purple-800 flex items-center gap-2 mb-3">
                  <Stethoscope className="w-5 h-5" />
                  Primary Diagnosis
                </h3>
                {isEditMode ? (
                  <Input
                    value={editForm.primary_diagnosis}
                    onChange={(e) => setEditForm(prev => ({ ...prev, primary_diagnosis: e.target.value }))}
                    placeholder="Enter primary diagnosis"
                    className="mt-1"
                  />
                ) : (
                  <p className="text-gray-700">{overview.primary_diagnosis || 'Not documented'}</p>
                )}
              </Card>

              {/* Emergency Contact Card */}
              <Card className="p-4 border-blue-200">
                <h3 className="font-semibold text-blue-800 flex items-center gap-2 mb-3">
                  <Phone className="w-5 h-5" />
                  Emergency Contact
                </h3>
                {isEditMode ? (
                  <div className="space-y-2">
                    <Input
                      value={editForm.emergency_contact_name}
                      onChange={(e) => setEditForm(prev => ({ ...prev, emergency_contact_name: e.target.value }))}
                      placeholder="Contact name"
                      className="text-sm"
                    />
                    <Input
                      value={editForm.emergency_contact_relationship}
                      onChange={(e) => setEditForm(prev => ({ ...prev, emergency_contact_relationship: e.target.value }))}
                      placeholder="Relationship"
                      className="text-sm"
                    />
                    <Input
                      value={editForm.emergency_contact_phone}
                      onChange={(e) => setEditForm(prev => ({ ...prev, emergency_contact_phone: e.target.value }))}
                      placeholder="Phone number"
                      className="text-sm"
                    />
                  </div>
                ) : overview.emergency_contact?.name ? (
                  <div className="text-sm">
                    <p className="font-medium">{overview.emergency_contact.name}</p>
                    <p className="text-gray-600">{overview.emergency_contact.relationship}</p>
                    <p className="text-blue-600">{overview.emergency_contact.phone}</p>
                  </div>
                ) : (
                  <p className="text-gray-500 text-sm">Not documented</p>
                )}
              </Card>

              {/* Fall Risk Level Card with Tooltip */}
              <Card className="p-4 border-amber-200">
                <h3 className="font-semibold text-amber-800 flex items-center gap-2 mb-3">
                  <Shield className="w-5 h-5" />
                  Fall Risk Level
                </h3>
                {isEditMode ? (
                  <div className="text-sm">
                    <div className={`inline-flex items-center px-3 py-1 rounded-full font-medium mb-3 ${
                      getFallRiskLevel(editForm.fall_risk_factors).color
                    }`}>
                      {getFallRiskLevel(editForm.fall_risk_factors).level.toUpperCase()} 
                      ({editForm.fall_risk_factors.length} factors)
                    </div>
                  </div>
                ) : (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium cursor-help ${
                        overview.care_level === 'high' ? 'bg-red-100 text-red-800' :
                        overview.care_level === 'moderate' ? 'bg-amber-100 text-amber-800' :
                        'bg-green-100 text-green-800'
                      }`}>
                        {overview.care_level?.toUpperCase() || 'LOW'}
                        {(overview.fall_risk_factors?.length > 0) && (
                          <span className="ml-1">({overview.fall_risk_factors?.length || 0})</span>
                        )}
                      </span>
                    </TooltipTrigger>
                    <TooltipContent className="bg-white border shadow-lg p-3 max-w-xs">
                      <div className="text-sm">
                        <p className="font-semibold text-gray-900 mb-2">Fall Risk Factors</p>
                        {(overview.fall_risk_factors?.length > 0) ? (
                          <ul className="list-disc list-inside space-y-1 text-gray-700">
                            {getFactorLabels(overview.fall_risk_factors).map((label, idx) => (
                              <li key={idx}>{label}</li>
                            ))}
                          </ul>
                        ) : (
                          <p className="text-gray-500">No specific risk factors documented</p>
                        )}
                      </div>
                    </TooltipContent>
                  </Tooltip>
                )}
              </Card>

              {/* Last Visit Card */}
              <Card className="p-4 border-green-200">
                <h3 className="font-semibold text-green-800 flex items-center gap-2 mb-3">
                  <Calendar className="w-5 h-5" />
                  Last Visit
                </h3>
                <p className="text-gray-700">
                  {overview.last_visit 
                    ? new Date(overview.last_visit).toLocaleDateString('en-US', {
                        weekday: 'short',
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })
                    : 'No visits recorded'}
                </p>
              </Card>

              {/* Address Card */}
              <Card className="p-4 border-gray-200">
                <h3 className="font-semibold text-gray-800 flex items-center gap-2 mb-3">
                  <User className="w-5 h-5" />
                  Address
                </h3>
                {isEditMode ? (
                  <Textarea
                    value={editForm.address}
                    onChange={(e) => setEditForm(prev => ({ ...prev, address: e.target.value }))}
                    placeholder="Client address"
                    rows={2}
                    className="text-sm"
                  />
                ) : (
                  <p className="text-gray-700 text-sm">{overview.address || 'Not documented'}</p>
                )}
              </Card>
            </div>

            {/* Fall Risk Factors Edit Panel - Only in Edit Mode */}
            {isEditMode && (
              <Card className="p-6 border-amber-200 bg-amber-50">
                <h3 className="font-semibold text-amber-900 mb-4 flex items-center gap-2">
                  <Shield className="w-5 h-5" />
                  Fall Risk Assessment Checklist
                </h3>
                <p className="text-sm text-amber-700 mb-4">
                  Select all applicable risk factors. Risk level: 0-1 = Low (Green), 2-3 = Moderate (Yellow), 4+ = High (Red)
                </p>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {/* Physical Factors */}
                  <div>
                    <h4 className="font-medium text-gray-900 mb-2 text-sm uppercase tracking-wide">Physical</h4>
                    <div className="space-y-2">
                      {fallRiskCategories.physical?.map(factor => (
                        <label key={factor.id} className="flex items-center gap-2 cursor-pointer">
                          <Checkbox
                            checked={editForm.fall_risk_factors.includes(factor.id)}
                            onCheckedChange={() => toggleFallRiskFactor(factor.id)}
                          />
                          <span className="text-sm text-gray-700">{factor.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Clinical Factors */}
                  <div>
                    <h4 className="font-medium text-gray-900 mb-2 text-sm uppercase tracking-wide">Clinical</h4>
                    <div className="space-y-2">
                      {fallRiskCategories.clinical?.map(factor => (
                        <label key={factor.id} className="flex items-center gap-2 cursor-pointer">
                          <Checkbox
                            checked={editForm.fall_risk_factors.includes(factor.id)}
                            onCheckedChange={() => toggleFallRiskFactor(factor.id)}
                          />
                          <span className="text-sm text-gray-700">{factor.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Environmental Factors */}
                  <div>
                    <h4 className="font-medium text-gray-900 mb-2 text-sm uppercase tracking-wide">Environmental</h4>
                    <div className="space-y-2">
                      {fallRiskCategories.environmental?.map(factor => (
                        <label key={factor.id} className="flex items-center gap-2 cursor-pointer">
                          <Checkbox
                            checked={editForm.fall_risk_factors.includes(factor.id)}
                            onCheckedChange={() => toggleFallRiskFactor(factor.id)}
                          />
                          <span className="text-sm text-gray-700">{factor.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Medication Factors */}
                  <div>
                    <h4 className="font-medium text-gray-900 mb-2 text-sm uppercase tracking-wide">Medications</h4>
                    <div className="space-y-2">
                      {fallRiskCategories.medications?.map(factor => (
                        <label key={factor.id} className="flex items-center gap-2 cursor-pointer">
                          <Checkbox
                            checked={editForm.fall_risk_factors.includes(factor.id)}
                            onCheckedChange={() => toggleFallRiskFactor(factor.id)}
                          />
                          <span className="text-sm text-gray-700">{factor.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Chronic Conditions */}
                  <div>
                    <h4 className="font-medium text-gray-900 mb-2 text-sm uppercase tracking-wide">Chronic Conditions</h4>
                    <div className="space-y-2">
                      {fallRiskCategories.chronic_conditions?.map(factor => (
                        <label key={factor.id} className="flex items-center gap-2 cursor-pointer">
                          <Checkbox
                            checked={editForm.fall_risk_factors.includes(factor.id)}
                            onCheckedChange={() => toggleFallRiskFactor(factor.id)}
                          />
                          <span className="text-sm text-gray-700">{factor.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Sensory Issues */}
                  <div>
                    <h4 className="font-medium text-gray-900 mb-2 text-sm uppercase tracking-wide">Sensory Issues</h4>
                    <div className="space-y-2">
                      {fallRiskCategories.sensory_issues?.map(factor => (
                        <label key={factor.id} className="flex items-center gap-2 cursor-pointer">
                          <Checkbox
                            checked={editForm.fall_risk_factors.includes(factor.id)}
                            onCheckedChange={() => toggleFallRiskFactor(factor.id)}
                          />
                          <span className="text-sm text-gray-700">{factor.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              </Card>
            )}
          </div>
        </TooltipProvider>
      )}

      {activeSection === 'notes' && (
        <div className="space-y-6">
          {/* Voice Recording Section */}
          <Card className="p-6 border-purple-200">
            <h3 className="font-semibold text-purple-900 mb-4 flex items-center gap-2">
              <Mic className="w-5 h-5" />
              AI Voice Charting
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              Record your shift observations verbally. The AI will transcribe and format them into professional clinical notes.
            </p>
            <VoiceRecorder 
              clientId={clientId} 
              shiftId={shiftId}
              onNoteConfirmed={handleNoteConfirmed}
              onSuggestedTemplate={(template) => {
                setAiSuggestedTemplate(template);
              }}
              onWoundData={(data) => {
                setAiWoundData(data);
              }}
            />
          </Card>

          {/* Progress Notes List */}
          <Card className="p-6">
            <h3 className="font-semibold text-gray-900 mb-4">Recent Progress Notes</h3>
            {progressNotes.length === 0 ? (
              <p className="text-gray-500 text-center py-8">No progress notes yet</p>
            ) : (
              <div className="space-y-4">
                {progressNotes.map(note => (
                  <div key={note.id} className="border-b pb-4 last:border-0">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <span className="font-medium text-gray-900">{note.caregiver_name}</span>
                        {note.ai_generated && (
                          <span className="ml-2 px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded">
                            AI-Assisted
                          </span>
                        )}
                      </div>
                      <span className="text-xs text-gray-500">
                        {new Date(note.created_at).toLocaleString()}
                      </span>
                    </div>
                    <p className="text-gray-700 whitespace-pre-wrap text-sm">{note.content}</p>
                    {note.signature_confirmed && (
                      <p className="text-xs text-green-600 mt-2 flex items-center gap-1">
                        <Check className="w-3 h-3" />
                        Signed at {new Date(note.signature_timestamp).toLocaleString()}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>
      )}

      {activeSection === 'vitals' && (
        <Card className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="font-semibold text-gray-900">Vital Signs History</h3>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => setShowVitalsForm(!showVitalsForm)}
              data-testid="record-vitals-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Record Vitals
            </Button>
          </div>
          
          {/* Record Vitals Form */}
          {showVitalsForm && (
            <Card className="p-4 mb-6 border-2 border-purple-200 bg-purple-50">
              <h4 className="font-semibold text-purple-900 mb-4 flex items-center gap-2">
                <Activity className="w-5 h-5" />
                Record New Vital Signs
              </h4>
              <form onSubmit={handleVitalsSubmit} className="space-y-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {/* Blood Pressure */}
                  <div className="col-span-2">
                    <Label className="text-sm font-medium text-gray-700">Blood Pressure (mmHg)</Label>
                    <div className="flex items-center gap-2 mt-1">
                      <Input
                        type="number"
                        placeholder="Systolic"
                        value={vitalsForm.blood_pressure_systolic}
                        onChange={(e) => setVitalsForm({...vitalsForm, blood_pressure_systolic: e.target.value})}
                        className="w-full"
                        min="60"
                        max="250"
                        data-testid="bp-systolic-input"
                      />
                      <span className="text-gray-500">/</span>
                      <Input
                        type="number"
                        placeholder="Diastolic"
                        value={vitalsForm.blood_pressure_diastolic}
                        onChange={(e) => setVitalsForm({...vitalsForm, blood_pressure_diastolic: e.target.value})}
                        className="w-full"
                        min="40"
                        max="150"
                        data-testid="bp-diastolic-input"
                      />
                    </div>
                  </div>
                  
                  {/* Heart Rate */}
                  <div>
                    <Label className="text-sm font-medium text-gray-700">Heart Rate (bpm)</Label>
                    <Input
                      type="number"
                      placeholder="e.g., 72"
                      value={vitalsForm.heart_rate}
                      onChange={(e) => setVitalsForm({...vitalsForm, heart_rate: e.target.value})}
                      className="mt-1"
                      min="30"
                      max="200"
                      data-testid="heart-rate-input"
                    />
                  </div>
                  
                  {/* Respiratory Rate */}
                  <div>
                    <Label className="text-sm font-medium text-gray-700">Resp Rate (/min)</Label>
                    <Input
                      type="number"
                      placeholder="e.g., 16"
                      value={vitalsForm.respiratory_rate}
                      onChange={(e) => setVitalsForm({...vitalsForm, respiratory_rate: e.target.value})}
                      className="mt-1"
                      min="8"
                      max="40"
                      data-testid="resp-rate-input"
                    />
                  </div>
                  
                  {/* Temperature */}
                  <div>
                    <Label className="text-sm font-medium text-gray-700">Temperature (°F)</Label>
                    <Input
                      type="number"
                      step="0.1"
                      placeholder="e.g., 98.6"
                      value={vitalsForm.temperature}
                      onChange={(e) => setVitalsForm({...vitalsForm, temperature: e.target.value})}
                      className="mt-1"
                      min="90"
                      max="110"
                      data-testid="temperature-input"
                    />
                  </div>
                  
                  {/* Oxygen Saturation */}
                  <div>
                    <Label className="text-sm font-medium text-gray-700">O2 Sat (%)</Label>
                    <Input
                      type="number"
                      placeholder="e.g., 98"
                      value={vitalsForm.oxygen_saturation}
                      onChange={(e) => setVitalsForm({...vitalsForm, oxygen_saturation: e.target.value})}
                      className="mt-1"
                      min="70"
                      max="100"
                      data-testid="o2-sat-input"
                    />
                  </div>
                  
                  {/* Blood Glucose */}
                  <div>
                    <Label className="text-sm font-medium text-gray-700">Blood Glucose (mg/dL)</Label>
                    <Input
                      type="number"
                      placeholder="e.g., 110"
                      value={vitalsForm.blood_glucose}
                      onChange={(e) => setVitalsForm({...vitalsForm, blood_glucose: e.target.value})}
                      className="mt-1"
                      min="20"
                      max="600"
                      data-testid="blood-glucose-input"
                    />
                  </div>
                  
                  {/* Pain Level */}
                  <div>
                    <Label className="text-sm font-medium text-gray-700">Pain Level (0-10)</Label>
                    <Input
                      type="number"
                      placeholder="0-10"
                      value={vitalsForm.pain_level}
                      onChange={(e) => setVitalsForm({...vitalsForm, pain_level: e.target.value})}
                      className="mt-1"
                      min="0"
                      max="10"
                      data-testid="pain-level-input"
                    />
                  </div>
                  
                  {/* Weight */}
                  <div>
                    <Label className="text-sm font-medium text-gray-700">Weight (lbs)</Label>
                    <Input
                      type="number"
                      step="0.1"
                      placeholder="e.g., 165"
                      value={vitalsForm.weight}
                      onChange={(e) => setVitalsForm({...vitalsForm, weight: e.target.value})}
                      className="mt-1"
                      min="50"
                      max="500"
                      data-testid="weight-input"
                    />
                  </div>
                </div>
                
                {/* Notes */}
                <div>
                  <Label className="text-sm font-medium text-gray-700">Notes</Label>
                  <Textarea
                    placeholder="Any additional observations..."
                    value={vitalsForm.notes}
                    onChange={(e) => setVitalsForm({...vitalsForm, notes: e.target.value})}
                    className="mt-1"
                    rows={2}
                    data-testid="vitals-notes-input"
                  />
                </div>
                
                {/* Submit Buttons */}
                <div className="flex justify-end gap-2 pt-2">
                  <Button 
                    type="button" 
                    variant="outline"
                    onClick={() => setShowVitalsForm(false)}
                  >
                    Cancel
                  </Button>
                  <Button 
                    type="submit"
                    className="bg-purple-600 hover:bg-purple-700 text-white"
                    disabled={savingVitals}
                    data-testid="save-vitals-btn"
                  >
                    {savingVitals ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Check className="w-4 h-4 mr-2" />
                        Save Vitals
                      </>
                    )}
                  </Button>
                </div>
              </form>
            </Card>
          )}
          
          {vitals.length === 0 ? (
            <p className="text-gray-500 text-center py-8">No vital signs recorded</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left">Date/Time</th>
                    <th className="px-4 py-2 text-left">BP</th>
                    <th className="px-4 py-2 text-left">HR</th>
                    <th className="px-4 py-2 text-left">Temp</th>
                    <th className="px-4 py-2 text-left">O2</th>
                    <th className="px-4 py-2 text-left">Pain</th>
                    <th className="px-4 py-2 text-left">Recorded By</th>
                  </tr>
                </thead>
                <tbody>
                  {vitals.map(v => (
                    <tr key={v.id} className="border-b">
                      <td className="px-4 py-2">{new Date(v.recorded_at).toLocaleString()}</td>
                      <td className="px-4 py-2">
                        {v.blood_pressure_systolic && v.blood_pressure_diastolic 
                          ? `${v.blood_pressure_systolic}/${v.blood_pressure_diastolic}`
                          : '--'}
                      </td>
                      <td className="px-4 py-2">{v.heart_rate || '--'}</td>
                      <td className="px-4 py-2">{v.temperature ? `${v.temperature}°F` : '--'}</td>
                      <td className="px-4 py-2">{v.oxygen_saturation ? `${v.oxygen_saturation}%` : '--'}</td>
                      <td className="px-4 py-2">{v.pain_level !== null ? `${v.pain_level}/10` : '--'}</td>
                      <td className="px-4 py-2">{v.caregiver_name}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      {activeSection === 'medications' && (
        <MARSection clientId={clientId} />
      )}

      {activeSection === 'history' && (
        <Card className="p-6">
          <h3 className="font-semibold text-gray-900 mb-6">Medical History</h3>
          
          {medicalHistory.length === 0 ? (
            <p className="text-gray-500 text-center py-8">No medical history documented</p>
          ) : (
            <div className="space-y-3">
              {medicalHistory.map(item => (
                <div key={item.id} className="flex items-start justify-between p-4 border rounded-lg">
                  <div>
                    <h4 className="font-medium text-gray-900">{item.condition}</h4>
                    {item.diagnosed_date && (
                      <p className="text-sm text-gray-500">Diagnosed: {item.diagnosed_date}</p>
                    )}
                    {item.notes && (
                      <p className="text-sm text-gray-600 mt-1">{item.notes}</p>
                    )}
                  </div>
                  <span className={`px-2 py-1 rounded text-xs ${
                    item.status === 'active' ? 'bg-red-100 text-red-800' :
                    item.status === 'chronic' ? 'bg-amber-100 text-amber-800' :
                    'bg-green-100 text-green-800'
                  }`}>
                    {item.status}
                  </span>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}

      {activeSection === 'care-plan' && (
        <CarePlanSection clientId={clientId} />
      )}

      {activeSection === 'incidents' && (
        <Card className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="font-semibold text-gray-900">Incident Reports</h3>
            <Button variant="outline" size="sm" className="text-red-600 border-red-300">
              <Plus className="w-4 h-4 mr-2" />
              Report Incident
            </Button>
          </div>
          
          {incidents.length === 0 ? (
            <p className="text-gray-500 text-center py-8">No incidents reported</p>
          ) : (
            <div className="space-y-4">
              {incidents.map(incident => (
                <div key={incident.id} className="p-4 border border-red-200 rounded-lg bg-red-50">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <AlertTriangle className="w-5 h-5 text-red-600" />
                      <span className="font-medium text-red-900 capitalize">
                        {incident.incident_type.replace('_', ' ')}
                      </span>
                    </div>
                    <span className={`px-2 py-1 rounded text-xs ${
                      incident.status === 'open' ? 'bg-red-200 text-red-800' :
                      incident.status === 'under_review' ? 'bg-amber-200 text-amber-800' :
                      'bg-green-200 text-green-800'
                    }`}>
                      {incident.status}
                    </span>
                  </div>
                  <p className="text-sm text-gray-700">{incident.description}</p>
                  <p className="text-xs text-gray-500 mt-2">
                    Reported by {incident.reporter_name} on {new Date(incident.incident_datetime).toLocaleString()}
                  </p>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}

      {/* Wound Care Section */}
      {activeSection === 'wound-care' && (
        <WoundCareSection 
          clientId={clientId}
          aiWoundData={aiWoundData}
          onAiDataUsed={() => setAiWoundData(null)}
        />
      )}

      {/* Family Log Placeholder */}
      {activeSection === 'family' && (
        <Card className="p-6">
          <h3 className="font-semibold text-gray-900 mb-4">Family Communication Log</h3>
          <p className="text-gray-500 text-center py-8">
            No family communications recorded
          </p>
        </Card>
      )}

      {/* AI Template Suggestion Prompt */}
      {showTemplatePrompt && pendingTemplate && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="ai-template-prompt-modal">
          <Card className="w-full max-w-md mx-4 p-6 animate-in fade-in zoom-in duration-200">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-purple-100 rounded-full">
                <Sparkles className="w-6 h-6 text-purple-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900 text-lg">AI Detected Care Need</h3>
                <p className="text-gray-600 mt-2">
                  Based on your clinical note, the AI detected content related to:
                </p>
                <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                  <p className="font-medium text-green-800 flex items-center gap-2">
                    <Heart className="w-5 h-5" />
                    {getTemplateDisplayName(pendingTemplate)}
                  </p>
                </div>
                <p className="text-sm text-gray-500 mt-3">
                  Would you like to add this care plan template for the client?
                </p>
              </div>
            </div>
            
            <div className="flex gap-3 mt-6">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => {
                  setShowTemplatePrompt(false);
                  setPendingTemplate(null);
                }}
                data-testid="dismiss-template-prompt-btn"
              >
                Maybe Later
              </Button>
              <Button
                className="flex-1 bg-purple-600 hover:bg-purple-700 text-white"
                onClick={handleAddTemplateFromPrompt}
                data-testid="add-template-prompt-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add to Care Plan
              </Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
